import React, { FC } from 'react';
import { StyledFooter } from './Footer.styles';
import { FooterProps } from './Footer.types';
import { Link } from '@marriott/mi-ui-library';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import DOMPurify from 'isomorphic-dompurify';
import logo from '../../../assets/images/lovetravels/alt-logo.svg';
import Image from 'next/image';

export const FooterWrapperConfig = {
  emptyLabel: 'LT Footer',
  isEmpty: false,
  resourceType: 'mi-aem-martech/components/lovetravels/footer',
};

const getEventName = (title: string) => {
  switch (title) {
    case 'Careers':
      return 'Footer-Careers';
    case 'Terms of Use':
      return 'Footer-TermsOfUse';
    case 'Program T&Cs':
      return 'Footer-TandC';
    case 'Privacy Center':
      return 'Footer-Privacy';
    case 'Your Privacy Rights':
      return 'Footer-PrivacyRights';
    case 'Digital Accessibility':
      return 'Footer-DigAccessibility';
    case 'Site Map':
      return 'Footer-SiteMap';
    case 'Help':
      return 'Footer-Help';
    default:
      return `Footer-${title.replace(/\s+/g, '')}`;
  }
};

export const FooterComp: FC<FooterProps> = (props: FooterProps) => {
  const { model } = props;

  return (
    <StyledFooter id="footer-wrapper-container" data-testid="footer">
      <footer className="lt-footer">
        <div className="container">
          <div className="row">
            <div className="col-md-6">
              <Image src={logo} alt="LoveTravels" className="logo" width={300} height={83} />
            </div>
            <div className="col-md-6 d-flex justify-content-md-end">
              <div className="lt-social">
                <a
                  href="https://www.facebook.com/marriottbonvoy"
                  target="_blank"
                  rel="noreferrer"
                  className="custom_click_track"
                  data-custom_click_track_value="Orange Footer|Facebook icon|Icon-Facebook"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
                    <g fill="none" fillRule="evenodd">
                      <circle cx="20" cy="20" r="20" fill="#000" />
                      <path
                        fill="#FF9662"
                        d="m24.81 21.3.578-3.764h-3.612v-2.443c0-1.03.504-2.034 2.122-2.034h1.642V9.854S24.05 9.6 22.625 9.6c-2.974 0-4.919 1.803-4.919 5.067v2.869H14.4V21.3h3.306v9.1h4.07v-9.1h3.034Z"
                      />
                    </g>
                  </svg>
                </a>
                <a
                  href="https://www.instagram.com/marriottbonvoy/"
                  target="_blank"
                  rel="noreferrer"
                  className="custom_click_track"
                  data-custom_click_track_value="Orange Footer|Instagram Icon|Icon-Instagram"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
                    <g fill="none" fillRule="evenodd">
                      <circle cx="20" cy="20" r="20" fill="#000" />
                      <path
                        fill="#FF9662"
                        d="M19.603 14.861a5.528 5.528 0 0 0-5.537 5.537 5.528 5.528 0 0 0 5.537 5.538 5.528 5.528 0 0 0 5.537-5.538 5.528 5.528 0 0 0-5.537-5.537Zm0 9.137c-1.98 0-3.6-1.614-3.6-3.6 0-1.985 1.615-3.6 3.6-3.6 1.986 0 3.6 1.615 3.6 3.6 0 1.986-1.619 3.6-3.6 3.6Zm7.056-9.363c0 .718-.579 1.291-1.292 1.291a1.292 1.292 0 1 1 1.292-1.292Zm3.667 1.31c-.082-1.73-.477-3.262-1.745-4.525-1.262-1.262-2.795-1.657-4.525-1.744-1.783-.101-7.127-.101-8.91 0-1.726.082-3.258.477-4.526 1.74-1.267 1.262-1.657 2.795-1.744 4.525-.101 1.783-.101 7.127 0 8.91.082 1.73.477 3.263 1.744 4.526 1.268 1.262 2.796 1.657 4.526 1.744 1.783.101 7.127.101 8.91 0 1.73-.082 3.263-.477 4.525-1.744 1.263-1.263 1.658-2.796 1.745-4.526.101-1.783.101-7.122 0-8.905Zm-2.304 10.82a3.645 3.645 0 0 1-2.053 2.053c-1.421.563-4.795.433-6.366.433-1.57 0-4.949.126-6.366-.433a3.645 3.645 0 0 1-2.053-2.053c-.564-1.422-.433-4.795-.433-6.367 0-1.57-.126-4.949.433-6.366a3.645 3.645 0 0 1 2.053-2.053c1.422-.563 4.795-.433 6.366-.433 1.571 0 4.95-.126 6.366.433a3.645 3.645 0 0 1 2.053 2.053c.564 1.422.434 4.795.434 6.366 0 1.572.13 4.95-.434 6.367Z"
                      />
                    </g>
                  </svg>
                </a>
                <a
                  href="https://www.linkedin.com/company/marriott-international/"
                  target="_blank"
                  rel="noreferrer"
                  className="custom_click_track"
                  data-custom_click_track_value="Orange Footer|LinkedIn Icon|Icon-LinkedIn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
                    <g fill="none" fillRule="evenodd">
                      <circle cx="20" cy="20" r="20" fill="#000" />
                      <path
                        fill="#FF9662"
                        d="M14.435 31.2H9.957V16.779h4.478v14.42ZM12.193 14.81c-1.432 0-2.593-1.186-2.593-2.618a2.593 2.593 0 1 1 5.187 0c0 1.432-1.162 2.618-2.594 2.618ZM31.195 31.2h-4.468v-7.02c0-1.674-.034-3.82-2.329-3.82-2.328 0-2.685 1.819-2.685 3.699v7.14H17.24V16.78h4.295v1.967h.063c.598-1.133 2.058-2.329 4.237-2.329 4.532 0 5.365 2.984 5.365 6.86V31.2h-.005Z"
                      />
                    </g>
                  </svg>
                </a>
                <a
                  href="https://twitter.com/marriottbonvoy"
                  target="_blank"
                  rel="noreferrer"
                  className="custom_click_track"
                  data-custom_click_track_value="Orange Footer|X Icon|Icon-X"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
                    <g fill="none" fillRule="evenodd">
                      <circle cx="20" cy="20" r="20" fill="#000" />
                      <path
                        fill="#FF9962"
                        fillRule="nonzero"
                        d="m11.046 11 7.336 9.928L11 29h1.661l6.463-7.067L24.346 29H30l-7.748-10.487L29.122 11h-1.66l-5.953 6.509L16.7 11h-5.654Zm2.444 1.239h2.597l11.47 15.522h-2.598L13.49 12.24Z"
                      />
                    </g>
                  </svg>
                </a>
                <a
                  href="https://www.youtube.com/marriott"
                  target="_blank"
                  rel="noreferrer"
                  className="custom_click_track"
                  data-custom_click_track_value="Orange Footer|YouTube Icon|Icon-YouTube"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
                    <g fill="none" fillRule="evenodd">
                      <circle cx="20" cy="20" r="20" fill="#000" />
                      <path
                        fill="#FF9662"
                        d="M26.88 12.8H13.12a3.52 3.52 0 0 0-3.52 3.52v7.36a3.52 3.52 0 0 0 3.52 3.52h13.76a3.52 3.52 0 0 0 3.52-3.52v-7.36a3.52 3.52 0 0 0-3.52-3.52Zm-2.806 7.34-6.24 3.2a.16.16 0 0 1-.234-.14v-6.4a.16.16 0 0 1 .234-.141l6.24 3.2a.16.16 0 0 1 0 .285v-.003Z"
                      />
                    </g>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid lt-legal">
          <div className="lt-legal-inner">
            <div
              className="lt-copyright"
              dangerouslySetInnerHTML={{ __html: model?.copyright ? DOMPurify.sanitize(model.copyright) : '' }}
              data-testid="copyright-text"
            />
            <div className="lt-legal-links">
              <Link id="ccpacontainer" linkClassName="footer-link" />
              <Link id="teconsent" linkClassName="footer-link" />
              {model?.footerLinks?.map((footerLink, index) => {
                const sanitizedTitle = DOMPurify.sanitize(footerLink.linkTitle || '').replace(/[|\r\n]/g, '');
                return (
                  <React.Fragment key={`footer-link-fragment-${index}`}>
                    <Link
                      key={`footer-link-${index}`}
                      text={footerLink.linkTitle}
                      linkHref={footerLink.linkPath}
                      linkClassName="footer-link custom_click_track"
                      custom_click_track_value={`Black Footer|${sanitizedTitle}|${getEventName(sanitizedTitle)}`}
                      target="_blank"
                    />
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </footer>
    </StyledFooter>
  );
};

/* eslint-disable @typescript-eslint/no-explicit-any */
export const LTFooter = (props: any) => {
  return (
    <EditableComponent {...props}>
      <div id="universal-footer" className="footer-container">
        <FooterComp {...props} />
      </div>
    </EditableComponent>
  );
};
